import { Droppable } from '@hello-pangea/dnd';
import { useEffect, useReducer, useRef } from 'react';
import { FormProvider, useFieldArray, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';

import { ErrorMessage, Title } from '~/components';

import type { DocumentFormSection } from '../types';

import { ComputerProgramAlert } from './ComputerProgramAlert/ComputerProgramAlert';
import { formStyle, linesStyle, tableSectionStyle } from './FormLines.css';
import { hasFocusableError, isFreeLine } from './FormLines.utils';
import { LinesDragDropContext } from './LinesDragDropContext/LinesDragDropContext';
import { LinesHeader } from './LinesHeader/LinesHeader';
import { Section } from './Section/Section';
import { SectionActions } from './SectionActions/SectionActions';
import { SectionLines } from './SectionLines/SectionLines';
import { PhotoAlbumSectionTitle } from './SectionTitle/PhotoAlbumSectionTitle';

export type FormWithLines = {
  contactId: number | null; // Used in product modal to determine the preferred language
  sections: DocumentFormSection[];
};

type Props<T extends FormWithLines> = {
  disableEntryLines?: boolean;
  form: T extends FormWithLines ? UseFormReturn<T> : never;
  i18nNamespace: 'invoices' | 'quotations';
  required?: boolean;
};

export const FormLines = <T extends FormWithLines>({ disableEntryLines = false, form, i18nNamespace, required = true }: Props<T>) => {
  const {
    control,
    formState: { errors, submitCount },
  } = form;

  const errorRef = useRef<HTMLParagraphElement>(null);

  // A failed submit focuses the first invalid field, which is what scrolls it into view — but the
  // `sections` root rules belong to `useFieldArray` and have no input to focus, so their error would
  // sit off screen and the submit button would look dead. Bring it into view by hand instead.
  useEffect(() => {
    if (submitCount === 0) return;

    const { errors } = form.formState;

    if (!errors.sections?.root || hasFocusableError(errors)) return;

    errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [form, submitCount]);

  const sectionsFieldArray = useFieldArray({
    control,
    name: 'sections',
    rules: {
      validate: {
        notEmpty: (sections) =>
          !required ||
          (sections.length === 1
            ? sections[0].lines.filter((line) => line.type === 'entry').length > 0
            : (sections.find((section) => section.isPhotoAlbum)?.lines ?? []).filter((line) => line.type === 'entry').length > 0),
        notAllFree: (sections) =>
          !required ||
          !sections
            .flatMap(({ lines }) => lines)
            .filter((line) => line.type === 'entry')
            .every((line) => isFreeLine(line)),
      },
    },
  });

  const mobileLineReducer = useReducer((state: string | null, indices: { lineIndex: number; sectionIndex: number } | null) => {
    if (!indices) return null;

    const lineId = `${indices.sectionIndex}-${indices.lineIndex}`;

    return state === lineId ? null : lineId;
  }, null);

  const { t } = useTranslation(['validation', 'invoices', 'quotations']);

  return (
    <FormProvider {...form}>
      <div className={formStyle}>
        <ComputerProgramAlert />

        <Title as="h2" styleAs="h3">
          {match(i18nNamespace)
            .with('invoices', () => t('invoices:createEdit.lines.title'))
            .with('quotations', () => t('quotations:createEdit.lines.title'))
            .exhaustive()}
        </Title>

        <table className={linesStyle}>
          <LinesHeader />

          <LinesDragDropContext sectionsFieldArray={sectionsFieldArray}>
            {sectionsFieldArray.fields.map((section, i) => (
              <Section
                key={section.id}
                mobileLineReducer={mobileLineReducer}
                required={required}
                section={section}
                sectionIndex={i}
                sectionsFieldArray={sectionsFieldArray}
              >
                {section.isPhotoAlbum && <PhotoAlbumSectionTitle />}

                {/* Use the section index as droppableId so it can be used to retrieve the correct section in onDragEnd */}
                <Droppable droppableId={`${i}`}>
                  {(provided) => (
                    <tbody {...provided.droppableProps} className={tableSectionStyle} ref={provided.innerRef}>
                      <SectionLines />
                      {provided.placeholder}
                    </tbody>
                  )}
                </Droppable>

                <SectionActions disableEntryLines={disableEntryLines} i18nNamespace={i18nNamespace} />
              </Section>
            ))}
          </LinesDragDropContext>
        </table>
        {errors.sections?.root?.type === 'notEmpty' && (
          <ErrorMessage ref={errorRef}>
            {match(i18nNamespace)
              .with('invoices', () => t('validation:invoice.lines.minimumOne'))
              .with('quotations', () => t('validation:quotation.lines.minimumOne'))
              .exhaustive()}
          </ErrorMessage>
        )}

        {errors.sections?.root?.type === 'notAllFree' && (
          <ErrorMessage ref={errorRef}>
            {match(i18nNamespace)
              .with('invoices', () => t('validation:invoice.lines.minimumOneNotFree'))
              .with('quotations', () => t('validation:quotation.lines.minimumOneNotFree'))
              .exhaustive()}
          </ErrorMessage>
        )}
      </div>
    </FormProvider>
  );
};
